import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  shareReplay,
  tap,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Customer, CustomerTypeEnum } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, RequesterUser } from 'server/shared/models/user';
import { rxFilterIsNotEmpty } from '../../helpers/filterIsNotEmpty';
import { SubscriptionService } from '../shared/subscription.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerService implements OnDestroy {
  // dispo OR requester customer
  dispoCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  readyDispoCustomer$ = this.dispoCustomer$.pipe(
    filter((customer): customer is Customer => !!customer),
    shareReplay(1),
  );
  readyDispoCustomer = firstValueFrom(this.readyDispoCustomer$);

  isAgency$ = this.readyDispoCustomer$.pipe(
    map((customer) => customer.customerType === CustomerTypeEnum.AGENCY),
    shareReplay(1),
  );

  customerIsSharingJobPool$ = this.readyDispoCustomer$.pipe(
    map((customer) => !!customer?.pool?.jobsSharedWithCustomerIds?.length),
    shareReplay(1),
  );

  sharedPoolCustomers$ = new BehaviorSubject<Customer[] | undefined>(undefined);
  readySharedPoolCustomers$ = this.sharedPoolCustomers$.pipe();

  poolerCustomers$ = new BehaviorSubject<Customer[] | undefined>(undefined);
  readyPoolerCustomers$ = this.poolerCustomers$.pipe(
    filter((customers): boolean => !!customers?.length),
    rxFilterIsNotEmpty(),
  );

  // mixed for pooler & dispo support of some components / customer feature flags
  customersOfUser$ = merge(
    this.readyDispoCustomer$.pipe(map((c) => [c])),
    this.readyPoolerCustomers$,
  );
  customersOfUser = firstValueFrom(this.customersOfUser$);

  private dispoCustomerTracker?: Tracker.Computation;
  private poolerCustomerTracker?: Tracker.Computation;

  sendCustomerToSubscriptionServiceEffect$ = this.readyDispoCustomer$.pipe(
    tap((customer) => {
      this.subscriptionService.dispoCustomerSubject$.next(customer);
    }),
  );

  constructor(
    private userService: UserService,
    private subscriptionService: SubscriptionService,
  ) {
    this.sendCustomerToSubscriptionServiceEffect$.subscribe();

    // if we don't wait for user isLoggedIn we have the issue that the readyDispoCustomer$ does not receive updates from subscriptions.
    // This was previously not needed because the service was loaded within router-outlet and therefore the timing issue did not occur
    // the isLoggedIn$ dependency had to be added when using the readyCustomer$ within the app-header component which was too early.
    this.userService.isLoggedIn$.subscribe((user) => {
      combineLatest([
        this.userService.readyUser$.pipe(
          filter(
            (user): user is DispoUser | RequesterUser =>
              user.role === RoleEnum.DISPO || user.role === RoleEnum.REQUESTER,
          ),
          map((user) => user.customerId),
          distinctUntilChanged(),
        ),
        this.subscriptionService.myCustomer.ready$,
      ]).subscribe(([customerId, _]) => {
        if (this.dispoCustomerTracker) {
          this.dispoCustomerTracker.stop();
        }
        this.dispoCustomerTracker = Tracker.autorun(() => {
          let customer = Customers.findOne(customerId);
          this.dispoCustomer$.next(customer || undefined);
          let sharedPoolCustomers = Customers.find({
            $or: [
              {
                'pool.jobsSharedWithCustomerIds': customer?._id,
              },
              {
                'pool.employmentsSharedWithCustomerIds': customer?._id,
              },
            ],
          }).fetch();

          this.sharedPoolCustomers$.next(
            sharedPoolCustomers?.sort((a, b) => a.name.localeCompare(b.name)) ||
              undefined,
          );
        });
      });
      combineLatest([
        this.userService.readyPooler$,
        this.subscriptionService.myCustomer.ready$,
      ]).subscribe(([user]) => {
        if (this.poolerCustomerTracker) {
          this.poolerCustomerTracker.stop();
        }
        this.poolerCustomerTracker = Tracker.autorun(() => {
          let customers = Customers.find({
            'pool.activeUserIds': Meteor.userId(),
          }).fetch();
          this.poolerCustomers$.next(customers || undefined);
        });
      });
    });
  }

  ngOnDestroy() {
    if (this.dispoCustomerTracker) {
      this.dispoCustomerTracker.stop();
    }
    if (this.poolerCustomerTracker) {
      this.poolerCustomerTracker.stop();
    }
  }
}
